/**
 * GraphQL transport (encrypted / compressed / base64 / direct bearer).
 * Kept separate from createApiEndpoint.js so data/global.js can import the API factory
 * without depending on the React entry module (index.js).
 */
import axios from "axios";
import { ApiUrl, Endpoints } from '../data/global.js';
import * as global from '../data/global.js';

/** RSA modulus|exponent from /email — required for /fetchc, /fetche, /fetchb. */
const hasRsaBolts = () => {
    const b = global.gw?.bolts;
    return typeof b === "string" && b.split("|").length === 2;
};

/** Same GraphQL body over /api/gw with Bearer JWT when the RSA channel is unavailable or returns no payload. */
const fetchDirectFallback = async (query, signal) => {
    // Access token is enough for /api/gw; `gw.auth` is the refresh token and may be absent (access-only login).
    if (global.gw?.token) {
        return await fetchDirect(query, signal);
    }
    return false;
};

export const fetchSwitch = async (query, output, signal, timeoutMs) => {
    if (output === "encrypted") {
        return await fetchEncrypted(query, signal);
    }
    else if (output === "base64") {
        return await fetchBase64(query, signal);
    }
    else if (output === "compressed") {
        return await fetchCompressed(query, signal);
    }
    else {
        return await fetchDirect(query, signal, timeoutMs);
    }
};

const fetchEncrypted = async (query) => {
    if (!hasRsaBolts()) {
        return await fetchDirectFallback(query);
    }
    let encrypted = await global.encryptMessageOutAsync(JSON.stringify(query), 512, 512, 512);
    if (!encrypted) {
        return await fetchDirectFallback(query);
    }

    let data = new FormData();
    data.append("email", global.gw.email);
    data.append("value", encrypted);

    let response = await axios({
        url: `${ApiUrl}${Endpoints.FetchEncrypted}`,
        method: 'post',
        timeout: 8000, // 8 second timeout
        headers: {
            "Content-Type": "multipart/form-data"
        },
        data: data
    }).then((response) => {
        if (response.data &&
            response.data.value &&
            response.data.value !== "") {
            try {
                let ein = response.data.value;
                let din = global.gw.nuts.decrypt(ein, "base64");
                let decodedBase64 = global.base64ToString(din.toString());
                let json = JSON.parse(decodedBase64);
                return global.base64StringToArrayBuffer(json.Value.value);

            } catch (e) {
                return false;
            }
        }
    }).catch(function (error) {
        if (error.response && error.response.status === 401) {
            throw error;
        }
        return false;
    });
    let decompressed = await global.decompress(response, "gzip");
    return JSON.parse(decompressed);
};

const fetchCompressed = async (query, signal) => {
    // After login we have a Bearer JWT for `/api/gw`. Prefer it over RSA `/fetchc` even when `bolts`
    // exist from `/email` — otherwise we still hit `/fetchc` and get tiny 200 bodies when decrypt fails.
    if (global.gw?.token) {
        return await fetchDirect(query, signal);
    }

    if (!hasRsaBolts()) {
        return await fetchDirectFallback(query, signal);
    }

    let encrypted = await global.encryptMessageOutAsync(JSON.stringify(query), 512, 512, 512);
    if (!encrypted) {
        return await fetchDirectFallback(query, signal);
    }

    let data = new FormData();
    data.append("email", global.gw.email);
    data.append("value", encrypted);

    const axiosConfig = {
        url: `${ApiUrl}${Endpoints.FetchCompressed}`,
        method: 'post',
        timeout: 30000, // 30 second timeout (increased from 8)
        headers: {
            "Content-Type": "multipart/form-data"
        },
        data: data
    };
    if (signal) {
        axiosConfig.signal = signal;
    }

    let response = await axios(axiosConfig).then((response) => {
        if (response && response.status === 401) {
            const authError = new Error('401 Unauthorized');
            authError.response = { status: 401 };
            throw authError;
        }

        if (response.data &&
            response.data.value &&
            response.data.value !== "") {
            try {
                const arrayBuffer = global.base64StringToArrayBuffer(response.data.value);
                return arrayBuffer;

            } catch (e) {
                return false;
            }
        }
        return false;
    }).catch(function (error) {
        if (error.response && error.response.status === 401) {
            throw error;
        }
        if (global.gw.token && global.gw.auth && !error.response) {
        }
        return false;
    });

    if (!response) {
        return await fetchDirectFallback(query, signal);
    }
    try {
        let decompressed = await global.decompress(response, "gzip");
        const result = JSON.parse(decompressed);
        return result;
    } catch (e) {
        return await fetchDirectFallback(query, signal);
    }
};

const fetchBase64 = async (query) => {
    if (!hasRsaBolts()) {
        return await fetchDirectFallback(query);
    }
    let encrypted = await global.encryptMessageOutAsync(JSON.stringify(query), 512, 512, 512);
    if (!encrypted) {
        return await fetchDirectFallback(query);
    }

    let data = new FormData();
    data.append("email", global.gw.email);
    data.append("value", encrypted);

    return await axios({
        url: `${ApiUrl}${Endpoints.FetchBase64}`,
        method: 'post',
        timeout: 8000, // 8 second timeout
        headers: {
            "Content-Type": "multipart/form-data"
        },
        data: data
    }).then((response) => {
        if (response.data &&
            response.data.value &&
            response.data.value !== "") {
            try {
                return global.serializeDataIn(response);
            } catch (e) {
                return false;
            }
        }
        return false;
    }).catch((error) => {
        return false;
    });
};

const fetchDirect = async (query, signal, timeoutMs = 30000) => {
    const token = global.gw?.token;
    if (typeof token !== 'string' || token.length === 0) {
        return Promise.resolve(false);
    }
    const config = {
        url: `${global.ApiUrl}${global.Endpoints.GrainWave}`,
        method: 'post',
        timeout: timeoutMs,
        headers: {
            'Content-Type': 'application/json',
            "Authorization": "Bearer " + token,
        },
        data: query
    };
    if (signal) {
        config.signal = signal;
    }
    return axios(config).then(response => {
        return response.data;
    }).catch(function (error) {
        if (error.response && error.response.status === 401) {
            throw error;
        }
        if (error.message && (error.message.includes("401") || error.message.includes("Unauthorized"))) {
            const authError = new Error(error.message);
            authError.response = { status: 401 };
            throw authError;
        }
        return false;
    });
};
